"use client";

import { formatDateLocal } from "@/shared/lib/date-time";
import { AreaChart } from "@/shared/ui/AreaChart";
import { Card, CardContent } from "@/components/ui/card";

import { useMemo } from "react";
import type { ExpiryLotRow } from "../../../domain/repo/get-expiry-lots";
import type { ExpiryLotsParams, InventoryParams } from "../../api/client";
import { useExpiryLotsQuery, useWastageReportQuery } from "../../api/queries";

type InventoryReportsContentProps = {
  filters: InventoryParams;
  onPaginationChange: (offset: number, limit: number) => void;
  limit: number;
  offset: number;
};

export function InventoryReportsContent({
  filters,
  onPaginationChange,
  limit,
  offset,
}: InventoryReportsContentProps) {
  const expiryLotsParams: ExpiryLotsParams = useMemo(
    () => ({
      ...filters,
      limit,
      offset,
    }),
    [filters, limit, offset],
  );

  const { data: expiryLotsData, isLoading: isLoadingExpiryLots } =
    useExpiryLotsQuery(expiryLotsParams);
  const { data: wastageData, isLoading: isLoadingWastage } =
    useWastageReportQuery(filters);

  // Format wastage data for AreaChart
  const wastageChartData = useMemo(() => {
    if (!wastageData || wastageData.length === 0) return [];

    // Group by period and sum up
    const grouped = new Map<string, number>();
    for (const item of wastageData) {
      const existing = grouped.get(item.period) ?? 0;
      grouped.set(item.period, existing + item.totalCostLak);
    }

    return Array.from(grouped.entries())
      .map(([period, cost]) => ({
        period,
        cost,
      }))
      .sort((a, b) => a.period.localeCompare(b.period));
  }, [wastageData]);

  if (isLoadingExpiryLots || isLoadingWastage) {
    return (
      <div className="space-y-4">
        <Card className="rounded-xl border bg-card">
          <CardContent className="p-6">
            <div className="h-64 animate-pulse rounded bg-muted" />
          </CardContent>
        </Card>
        <Card className="rounded-xl border bg-card">
          <CardContent className="p-6">
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <div key={i} className="h-12 animate-pulse rounded bg-muted" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const totalPages = Math.ceil((expiryLotsData?.total ?? 0) / limit);
  const currentPage = Math.floor(offset / limit) + 1;

  return (
    <div className="space-y-4">
      {/* Wastage Trend Chart */}
      <Card className="rounded-xl border bg-card">
        <CardContent className="">
          <h3 className="mb-4 font-semibold text-lg">ແນວໂນ້ມຂອງເສຍ (ຄ່າໃຊ້ຈ່າຍ)</h3>
          {wastageChartData.length === 0 ? (
            <div className="flex h-64 items-center justify-center text-muted-foreground">
              ບໍ່ມີຂໍ້ມູນ
            </div>
          ) : (
            <div className="h-64 w-full">
              <AreaChart
                data={wastageChartData}
                config={{
                  cost: {
                    label: "ຄ່າໃຊ້ຈ່າຍ (LAK)",
                    color: "hsl(var(--chart-1))",
                  },
                }}
                xDataKey="period"
                valueDataKey="cost"
                xAxisTickFormatter={(value) => {
                  // Format period based on groupBy
                  if (filters.groupBy === "month") {
                    return value.slice(5); // Show MM only
                  }
                  if (filters.groupBy === "week") {
                    return value.split("-")[1] || value; // Show WXX
                  }
                  return value.slice(5); // Show MM-DD
                }}
                className="h-full w-full"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Expiry Lots Table */}
      <Card className="rounded-xl border bg-card p-0">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    Lot No
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    ສາຂາ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    ສິນຄ້າ
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    ວັນທີຫມົດອາຍຸ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-sm">
                    ຈຳນວນຄົງເຫຼືອ
                  </th>
                  <th className="px-4 py-3 text-right font-medium text-sm">
                    ມູນຄ່າ (LAK)
                  </th>
                  <th className="px-4 py-3 text-left font-medium text-sm">
                    ວັນທີຮັບ
                  </th>
                </tr>
              </thead>
              <tbody>
                {!expiryLotsData || expiryLotsData.items.length === 0 ? (
                  <tr>
                    <td
                      colSpan={7}
                      className="px-4 py-8 text-center text-muted-foreground"
                    >
                      ບໍ່ມີຂໍ້ມູນສຳລັບການກອງທີ່ເລືອກ
                    </td>
                  </tr>
                ) : (
                  expiryLotsData.items.map((item: ExpiryLotRow) => {
                    const expiryDateStr = item.expiryDate
                      ? formatDateLocal(item.expiryDate)
                      : "–";
                    const daysUntilExpiryStr =
                      item.daysUntilExpiry !== null
                        ? `${item.daysUntilExpiry} ວັນ`
                        : "–";
                    const valueLak =
                      item.costPerUnitLak && item.qtyRemaining
                        ? item.costPerUnitLak * item.qtyRemaining
                        : 0;

                    return (
                      <tr key={item.id} className="border-b hover:bg-muted/50">
                        <td className="px-4 py-3 font-mono text-sm">
                          {item.lotNo}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {item.branchName || "–"}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {item.itemName || "–"}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div>{expiryDateStr}</div>
                          {item.daysUntilExpiry !== null && (
                            <div
                              className={`text-xs ${
                                item.daysUntilExpiry <= 7
                                  ? "text-red-600"
                                  : item.daysUntilExpiry <= 30
                                    ? "text-yellow-600"
                                    : "text-muted-foreground"
                              }`}
                            >
                              {daysUntilExpiryStr}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm tabular-nums">
                          {item.qtyRemaining.toLocaleString("lo-LA", {
                            maximumFractionDigits: 2,
                          })}
                        </td>
                        <td className="px-4 py-3 text-right text-sm tabular-nums">
                          {valueLak.toLocaleString("lo-LA")}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {formatDateLocal(item.receivedAt)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {expiryLotsData && expiryLotsData.total > 0 && (
            <div className="flex items-center justify-between border-t px-4 py-3">
              <div className="text-muted-foreground text-sm">
                ສະແດງ {offset + 1} -{" "}
                {Math.min(offset + limit, expiryLotsData.total)} ຈາກ{" "}
                {expiryLotsData.total.toLocaleString("lo-LA")} ລາຍການ
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    onPaginationChange(Math.max(0, offset - limit), limit)
                  }
                  disabled={offset === 0}
                  className="rounded-md border px-3 py-1 text-sm transition-colors hover:bg-muted disabled:opacity-50"
                >
                  ກ່ອນໜ້າ
                </button>
                <span className="text-muted-foreground text-sm">
                  ໜ້າ {currentPage} ຈາກ {totalPages}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    onPaginationChange(
                      Math.min(expiryLotsData.total - limit, offset + limit),
                      limit,
                    )
                  }
                  disabled={offset + limit >= expiryLotsData.total}
                  className="rounded-md border px-3 py-1 text-sm transition-colors hover:bg-muted disabled:opacity-50"
                >
                  ຖັດໄປ
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
